import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import SessionDetailsDialog from 'components/session-details-dialog'
import EventDetailsDialog from 'pages/pga-coach/bookings/events/event-details-dialog'
import { parseEvent } from 'utils/eventUtils'
import EventBusyIcon from 'components/material-symbol/icons/EventBusy'
import { SCHEDULE_EVENT_TYPES } from 'pages/pga-coach/dashboard/todays-schedule/schedule-event-colors'
import { getCalendarEventTheme } from './calendar-event-theme'
import { SCHEDULE_AVAILABILITY_BLOCKED_STRIPE } from './schedule-availability-styles'
import {
  formatMonthEventTimeLabel,
  getBookingEventKey,
  useBookingDisplay,
} from './calendar-utils'
import useCalendarStyles from './calendar-styles'

const useBookingDetailsInteraction = ({
  booking,
  eventType,
  onReschedule,
  onPayment,
  onCancellation,
}) => {
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false)
  const [groupDetailsView, setGroupDetailsView] = useState(false)
  const isSession = booking.__typename === 'Session'
  const isBlocked = eventType === SCHEDULE_EVENT_TYPES.BLOCKED_TIME
  const interactive = !isBlocked

  const handleClick = event => {
    if (!interactive) {
      return
    }
    event?.stopPropagation?.()
    if (isSession) {
      setSessionDialogOpen(true)
      return
    }
    setGroupDetailsView('detail')
  }

  const handleKeyDown = event => {
    if (!interactive) {
      return
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleClick(event)
    }
  }

  const dialogs = interactive ? (
    isSession ? (
      <SessionDetailsDialog
        isOpen={sessionDialogOpen}
        setIsOpen={setSessionDialogOpen}
        booking={booking}
        onReschedule={onReschedule}
        onPayment={onPayment}
        onCancellation={onCancellation}
      />
    ) : (
      <EventDetailsDialog
        isOpen={groupDetailsView}
        setIsOpen={setGroupDetailsView}
        event={parseEvent(booking)}
      />
    )
  ) : null

  return {
    interactive,
    handleClick,
    handleKeyDown,
    dialogs,
  }
}

const CalendarEventBlock = ({
  booking,
  style,
  compact = false,
  onReschedule,
  onPayment,
  onCancellation,
}) => {
  const classes = useCalendarStyles()
  const display = useBookingDisplay(booking)
  const theme = getCalendarEventTheme(display.eventType)
  const {
    interactive,
    handleClick,
    handleKeyDown,
    dialogs,
  } = useBookingDetailsInteraction({
    booking,
    eventType: display.eventType,
    onReschedule,
    onPayment,
    onCancellation,
  })
  const isBlocked = display.eventType === SCHEDULE_EVENT_TYPES.BLOCKED_TIME
  const blockedTitle =
    display.title && display.title !== 'Blocked time' ? display.title : null
  const showBlockedTitle = isBlocked && blockedTitle && !compact

  return (
    <>
      <Box
        className={`${classes.eventBlock} ${
          interactive ? classes.eventBlockInteractive : ''
        } ${isBlocked ? classes.eventBlockBlocked : ''}`}
        style={{
          ...style,
          backgroundColor: theme.backgroundColor,
          color: theme.color,
          ...(isBlocked
            ? {
              backgroundImage: SCHEDULE_AVAILABILITY_BLOCKED_STRIPE,
              border: '1px solid rgba(176, 184, 193, 0.45)',
            }
            : {}),
        }}
        onClick={interactive ? handleClick : undefined}
        onKeyDown={interactive ? handleKeyDown : undefined}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        data-testid="calendar-event-block"
        data-event-type={display.eventType}
        title={
          isBlocked
            ? [blockedTitle || 'Blocked time', display.subtitle]
              .filter(Boolean)
              .join(' · ')
            : undefined
        }
      >
        {isBlocked && !compact ? (
          <EventBusyIcon
            className={classes.eventBlockIcon}
            fill={0}
            htmlColor={theme.color}
            aria-hidden
          />
        ) : null}
        <Box className={classes.eventBlockText}>
          {isBlocked ? (
            <>
              {showBlockedTitle ? (
                <Typography
                  className={classes.eventBlockedTitle}
                  style={{ color: theme.color }}
                  component="span"
                >
                  {blockedTitle}
                </Typography>
              ) : null}
              {display.subtitle ? (
                <Typography
                  className={classes.eventBlockedSubtitle}
                  style={{ color: theme.color }}
                  component="span"
                >
                  {display.subtitle}
                </Typography>
              ) : null}
            </>
          ) : (
            <>
              <Typography
                className={classes.eventTitle}
                style={{ color: theme.color }}
                component="span"
              >
                {display.title}
              </Typography>
              {!compact && display.subtitle ? (
                <Typography
                  className={classes.eventSubtitle}
                  style={{ color: theme.color }}
                  component="span"
                >
                  {display.subtitle}
                </Typography>
              ) : null}
            </>
          )}
        </Box>
      </Box>
      {dialogs}
    </>
  )
}

CalendarEventBlock.propTypes = {
  booking: PropTypes.object.isRequired,
  style: PropTypes.object,
  compact: PropTypes.bool,
  onReschedule: PropTypes.func,
  onPayment: PropTypes.func,
  onCancellation: PropTypes.func,
}

export const CalendarMonthEventPill = ({
  booking,
  onReschedule,
  onPayment,
  onCancellation,
}) => {
  const classes = useCalendarStyles()
  const display = useBookingDisplay(booking)
  const theme = getCalendarEventTheme(display.eventType)
  const isBlocked = display.eventType === SCHEDULE_EVENT_TYPES.BLOCKED_TIME
  const timeLabel = formatMonthEventTimeLabel(booking)
  const {
    interactive,
    handleClick,
    handleKeyDown,
    dialogs,
  } = useBookingDetailsInteraction({
    booking,
    eventType: display.eventType,
    onReschedule,
    onPayment,
    onCancellation,
  })

  return (
    <>
      <Box
        className={`${classes.monthEventPill} ${
          interactive ? classes.monthEventPillInteractive : ''
        } ${isBlocked ? classes.monthEventPillBlocked : ''}`}
        style={{
          backgroundColor: theme.backgroundColor,
          color: theme.color,
          ...(isBlocked
            ? {
              backgroundImage: SCHEDULE_AVAILABILITY_BLOCKED_STRIPE,
              border: '1px solid rgba(176, 184, 193, 0.45)',
            }
            : {}),
        }}
        onClick={interactive ? handleClick : undefined}
        onKeyDown={interactive ? handleKeyDown : undefined}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        data-testid={`month-event-${getBookingEventKey(booking)}`}
        data-event-type={display.eventType}
      >
        <span className={classes.monthEventTime}>{timeLabel}</span>
        <span className={classes.monthEventTitle}>{display.title}</span>
      </Box>
      {dialogs}
    </>
  )
}

CalendarMonthEventPill.propTypes = {
  booking: PropTypes.object.isRequired,
  onReschedule: PropTypes.func,
  onPayment: PropTypes.func,
  onCancellation: PropTypes.func,
}

export default CalendarEventBlock
